import { omit } from 'lodash-es';
import queryString from 'query-string';

const imageView2FOP = {
    /**
     * 解析云处理参数字符串
     * @param str - 云处理参数字符串
     * @returns 解析后的参数对象
     */
    parse: (str: string): Record<string, any> => {
        const params: Record<string, any> = {};

        if (!str || !str.startsWith('imageView2/')) {
            return params;
        }

        const parts = str.split('/');

        // 第一个部分是 imageView2，第二个部分就是 mode 的值
        if (parts.length > 1) {
            params.mode = parts[1];
        }

        // 从第三个部分开始处理键值对
        for (let i = 2; i < parts.length; i += 2) {
            const key = parts[i];
            const value = parts[i + 1];

            if (value === undefined) continue;

            switch (key) {
                case 'w':
                    params.w = parseInt(value, 10);
                    break;
                case 'h':
                    params.h = parseInt(value, 10);
                    break;
                case 'format':
                    params.format = value; // jpg, gif, png, webp
                    break;
                case 'interlace':
                    params.interlace = parseInt(value, 10); // 0, 1, 2
                    break;
                case 'q': {
                    const quality = parseInt(value, 10);
                    if (quality >= 1 && quality <= 100) {
                        params.q = quality;
                    }
                    break;
                }
                case 'static':
                    params.static = parseInt(value, 10) === 1; // 0, 1
                    break;
                case 'ignore-error':
                    params.ignoreError = parseInt(value, 10) === 1; // 1
                    break;
                case 'compress': {
                    const compressLevel = parseInt(value, 10);
                    if (compressLevel >= 1 && compressLevel <= 11) {
                        params.compress = compressLevel;
                    }
                    break;
                }
                case 'lossless': {
                    const lossless = parseInt(value, 10);
                    if (lossless >= 0 && lossless <= 100) {
                        params.lossless = lossless;
                    }
                    break;
                }
                case 'flatten':
                    params.flatten = parseInt(value, 10) === 1; // 0, 1
                    break;
                case 'density': {
                    const density = parseInt(value, 10);
                    if (density > 0) {
                        params.density = density;
                    }
                    break;
                }
                default:
                    // 对于未知参数，直接保留原始值
                    params[key] = value;
            }
        }

        return params;
    },

    /**
     * 序列化云处理参数对象
     * @param params - 参数对象
     * @returns 序列化后的字符串
     */
    stringify: (params: Record<string, any>): string => {
        if (!params.mode) return '';

        const parts = ['imageView2'];

        // mode 参数特殊处理，直接跟在 imageView2 后面
        parts.push(params.mode.toString());

        // 其他参数按键值对处理
        const keyMappings = [
            { key: 'w', str: 'w' },
            { key: 'h', str: 'h' },
            { key: 'format', str: 'format' },
            { key: 'interlace', str: 'interlace' },
            { key: 'q', str: 'q' },
            { key: 'static', str: 'static' },
            { key: 'ignoreError', str: 'ignore-error' },
            { key: 'compress', str: 'compress' },
            { key: 'lossless', str: 'lossless' },
            { key: 'flatten', str: 'flatten' },
            { key: 'density', str: 'density' },
        ];

        keyMappings.forEach((mapping) => {
            const value = params[mapping.key];
            if (value !== undefined) {
                // 云处理开关使用 1/0，兼容 parse 返回的布尔值
                parts.push(mapping.str, typeof value === 'boolean' ? (value ? '1' : '0') : value.toString());
            }
        });

        return parts.join('/');
    },
};

const watermarkFOP = {
    /**
     * 通用水印参数解析
     * @param str - 水印参数字符串
     * @returns 解析后的参数对象
     */
    parse: (str: string): Record<string, any> => {
        if (!str || !str.startsWith('watermark/')) {
            return {};
        }

        const parts = str.split('/');
        const type = parseInt(parts[1], 10);

        if (type === 1) {
            return watermarkFOP.parseImage(str);
        } else if (type === 2) {
            return watermarkFOP.parseText(str);
        }

        return {};
    },

    /**
     * 解析文字水印参数字符串
     * @param str - 文字水印参数字符串
     * @returns 解析后的参数对象
     */
    parseText: (str: string): Record<string, any> => {
        const params: Record<string, any> = { type: 2 };

        if (!str || !str.startsWith('watermark/2')) {
            return params;
        }

        const parts = str.split('/');

        // 从第三个部分开始处理键值对
        for (let i = 2; i < parts.length; i += 2) {
            const key = parts[i];
            const value = parts[i + 1];

            if (value === undefined) continue;

            switch (key) {
                case 'text':
                case 'font':
                case 'fill':
                    // 对Base64编码的参数进行解码
                    try {
                        params[key] = atob(value.replace(/_/g, '/').replace(/-/g, '+'));
                    } catch (e) {
                        console.error(e);
                        params[key] = value; // 如果解码失败，保留原始值
                    }
                    break;
                case 'fontsize':
                    params.fontSize = parseInt(value, 10);
                    break;
                case 'dissolve': {
                    const dissolve = parseInt(value, 10);
                    if (dissolve >= 1 && dissolve <= 100) {
                        params.dissolve = dissolve;
                    }
                    break;
                }
                case 'gravity':
                    params.gravity = value;
                    break;
                case 'dx':
                case 'dy':
                    params[key] = parseInt(value, 10);
                    break;
                default:
                    // 对于未知参数，直接保留原始值
                    params[key] = value;
            }
        }

        return params;
    },

    /**
     * 解析图片水印参数字符串
     * @param str - 图片水印参数字符串
     * @returns 解析后的参数对象
     */
    parseImage: (str: string): Record<string, any> => {
        const params: Record<string, any> = { type: 1 };

        if (!str || !str.startsWith('watermark/1')) {
            return params;
        }

        const parts = str.split('/');

        // 从第三个部分开始处理键值对
        for (let i = 2; i < parts.length; i += 2) {
            const key = parts[i];
            const value = parts[i + 1];

            if (value === undefined) continue;

            switch (key) {
                case 'image':
                    // 对Base64编码的图片URL进行解码
                    try {
                        params.image = decodeURIComponent(atob(value.replace(/_/g, '/').replace(/-/g, '+')));
                    } catch (e) {
                        params.image = value; // 如果解码失败，保留原始值
                    }
                    break;
                case 'dissolve': {
                    const dissolve = parseInt(value, 10);
                    if (dissolve >= 1 && dissolve <= 100) {
                        params.dissolve = dissolve;
                    }
                    break;
                }
                case 'gravity':
                    params.gravity = value;
                    break;
                case 'dx':
                case 'dy':
                case 'px':
                case 'py':
                    params[key] = parseInt(value, 10);
                    break;
                case 'whs':
                case 'ws':
                case 'hs':
                case 'wmax':
                case 'hmax': {
                    const floatValue = parseFloat(value);
                    if (floatValue > 0 && floatValue <= 1) {
                        params[key] = floatValue;
                    }
                    break;
                }
                default:
                    // 对于未知参数，直接保留原始值
                    params[key] = value;
            }
        }

        return params;
    },

    /**
     * 序列化水印参数对象
     * @param params - 参数对象
     * @returns 序列化后的字符串
     */
    stringify: (params: Record<string, any>): string => {
        if (!params.type) return '';

        const parts = ['watermark'];

        // type 参数特殊处理，直接跟在 watermark 后面
        parts.push(params.type.toString());

        // 根据水印类型选择不同的参数映射
        let keyMappings: Array<{ key: string; str: string; encode?: boolean }> = [];

        if (params.type === 1) {
            // 图片水印参数
            keyMappings = [
                { key: 'image', str: 'image', encode: true },
                { key: 'dissolve', str: 'dissolve' },
                { key: 'gravity', str: 'gravity' },
                { key: 'dx', str: 'dx' },
                { key: 'dy', str: 'dy' },
                { key: 'px', str: 'px' },
                { key: 'py', str: 'py' },
                { key: 'whs', str: 'whs' },
                { key: 'ws', str: 'ws' },
                { key: 'hs', str: 'hs' },
                { key: 'wmax', str: 'wmax' },
                { key: 'hmax', str: 'hmax' },
            ];
        } else {
            // 文字水印参数（默认类型2）
            keyMappings = [
                { key: 'text', str: 'text', encode: true },
                { key: 'font', str: 'font', encode: true },
                { key: 'fontSize', str: 'fontsize' },
                { key: 'fill', str: 'fill', encode: true },
                { key: 'dissolve', str: 'dissolve' },
                { key: 'gravity', str: 'gravity' },
                { key: 'dx', str: 'dx' },
                { key: 'dy', str: 'dy' },
            ];
        }

        keyMappings.forEach((mapping) => {
            if (params[mapping.key] !== undefined) {
                let value = params[mapping.key].toString();

                // 对需要Base64编码的参数进行处理
                if (mapping.encode) {
                    // URL安全的Base64编码
                    value = btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
                }

                parts.push(mapping.str, value);
            }
        });

        return parts.join('/');
    },
};

/**
 * @see https://cf.meitu.com/confluence/pages/viewpage.action?pageId=8489350
 */
export const FOP = {
    split: (url: string) => {
        const params = queryString.parseUrl(url);
        const keys = Object.keys(params.query);
        const fopCommand =
            keys.find((key) => {
                return /(watermark|imageView2)/gi.test(key);
            }) || '';

        return {
            baseURL: params.url,
            fop: fopCommand,
            query: omit({ ...params.query }, [fopCommand]),
        };
    },

    parse: (fopKey: string) => {
        if (!fopKey) return {};

        const fopValue = fopKey.split('|');

        const imageView2Value =
            fopValue.find((value) => {
                return /imageView2/gi.test(value);
            }) || '';

        const watermarkValue =
            fopValue.find((value) => {
                return /watermark/gi.test(value);
            }) || '';

        const imageView2Params = imageView2FOP.parse(imageView2Value);
        const watermarkParams = watermarkFOP.parse(watermarkValue);

        return {
            imageView2: imageView2Params,
            watermark: watermarkParams,
        };
    },

    stringify: (
        params: { imageView2?: Record<string, any>; watermark?: Record<string, any> },
        options: { baseURL?: string; query?: Record<string, any> } = {},
    ) => {
        const { baseURL = '', query = {} } = options;
        const originQueryString = queryString.stringify(query);

        let fopParams: string[] = [];

        if (params.watermark) {
            const watermarkString = watermarkFOP.stringify(params.watermark);

            if (watermarkString) {
                fopParams.push(watermarkString);
            }
        }

        if (params.imageView2) {
            const imageView2String = imageView2FOP.stringify(params.imageView2);

            if (imageView2String) {
                fopParams.push(imageView2String);
            }
        }

        const fopStr = fopParams.join('|');

        if (!baseURL && !originQueryString) {
            return fopStr;
        }

        // ATTENTION: 这里为什么只能采用拼接的方式，是因为如果使用 queryString.stringify 云处理参数会多一个 = 导致最后一个命令失效
        const finalQuery = [originQueryString, fopStr].filter(Boolean).join('&');
        const finalURL = finalQuery ? [baseURL, finalQuery].join('?') : baseURL;

        return finalURL;
    },
};
